import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { UserCaseStudy } from './UserCaseStudy';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Row, Message, Animate, ViewContext } from 'components/lib';
import { StudentRegistration } from 'views/auth/signup/studentVerification';
import Styles from './UserAssessmentRouter.module.scss'
import { useContext } from 'react';

export const UserAssessmentRouter = () => {
    const { assessmentId } = useParams();
    const viewContext = useContext(ViewContext);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validity, setValidity] = useState({
        is_active: false,
        isValid: false
    });

    const [responseSubmitted, setResponseSubmitted] = useState(null); // null -> Form, true -> Message, false -> Show Quiz
    const [quizCompleted, setQuizCompleted] = useState(false);
    const [assessmentData, setAssessmentData] = useState([]);
    const [userData, setUserData] = useState(null)

    useEffect(() => {
        const fetchAssessmentValidity = async () => {
            try {
                setLoading(true);
                setError(null);

                const endpoint = `/api/assessments/${assessmentId}/validity`

                const { data } = await axios.get(endpoint, {
                    headers: {
                        'Content-Type': 'application/json',
                    }
                });

                const { is_active, validity } = data.data;
                const currentDate = new Date();
                const validityDate = validity ? new Date(validity) : null;
                const isValid = validityDate ? currentDate <= validityDate : true; //if no expiry considering it as valid
                setValidity({
                    is_active: !!is_active,
                    isValid: !!isValid
                });
            } catch (err) {
                console.error('Error fetching assessment validity:', err);
                setError(err.response?.data?.message || 'Failed to load assessment data');
            } finally {
                setLoading(false);
            }
        };

        fetchAssessmentValidity();
    }, [assessmentId]);


    const handleUserSubmit = async (e, formData) => {
        e.preventDefault();
        try {
            setLoading(true);
            setError(null);

            const response = await axios.post(`/api/assessments/${assessmentId}`, formData, {
                headers: { 'Content-Type': 'application/json' }
            });
            setUserData(response?.data?.data)
            const isSubmitted = response.data?.data?.status === 'completed'

            if (isSubmitted) {
                setResponseSubmitted(true); // Already submitted
            } else {
                setResponseSubmitted(false); // Show questions
                fetchAssessmentData(response?.data.data?.responses);
            }
        } catch (err) {
            console.error('Error submitting user data:', err);
            viewContext.notification.show('Error registering user try again', 'error', true)
        } finally {

            setLoading(false);
        }
    };


    const fetchAssessmentData = async (responses) => {
        try {
            const response = await axios.get(`/api/assessments/${assessmentId}`);
            setAssessmentData({
                ...response.data.data,
                responses: responses || []
            });
        } catch (err) {
            console.error('Error fetching questions:', err);
            setError('Failed to load quiz questions');
        }
    };


    const handleAssessmentSubmit = async (quizResponses) => {
        try {
            setLoading(true);
            await axios.patch(`/api/assessments/${assessmentId}`, { responses: quizResponses, email: userData.email, status: 'completed' });
            setResponseSubmitted(true);
            setQuizCompleted(true)
        } catch (err) {
            console.error('Error submitting quiz:', err);
            viewContext.notification.show('Error submitting quiz try again', 'error', true)
        } finally {
            setLoading(false);
        }
    };

    const handleAssessmentSave = async (quizResponses) => {
        try {
            await axios.patch(`/api/assessments/${assessmentId}`, { responses: quizResponses, email: userData.email, status: 'started' });
            viewContext.notification.show('Successfully Saved Response', 'success', true)
        } catch (err) {
            console.error('Error saving user response:', err);
            viewContext.notification.show('Error Saving Response. Try again', 'error', true)
        }
    };

    const renderMessage = (type, title, text, buttonText, buttonLink) => (
        <Animate>
            <Row>
                <div style={{ width: '100%', margin: '0 auto' }}>
                    <Message
                        type={type}
                        title={title}
                        text={text}
                    />
                </div>
            </Row>
        </Animate>
    );


    if (loading) {
        return <LoadingBackdrop isOpen={loading} />
    }

    if (error || !assessmentData) {
        return (
            <div id='ancestorContainer' className={Styles.ancestorContainer}>
                {renderMessage('error', 'Error Loading Assessment', 'There was some trouble loading the assessment. Please try again later.')}
            </div>
        )

    }

    if (!validity.is_active || !validity.isValid) {
        return (
            <div id='ancestorContainer' className={Styles.ancestorContainer}>
                {renderMessage('error', 'Assessment Not Available', 'This assessment is no longer available. The assessment has either been closed or not accepting responses anymore.')}
            </div>
        )
    }

    const renderAssessment = (type) => {
        switch (type) {
            case 'case-study':
                return <UserCaseStudy
                    data={assessmentData}
                    onSubmit={handleAssessmentSubmit}
                    onSave={handleAssessmentSave}
                    userEmail={userData.email}
                />
            case 'quiz':
                //Return UserQuiz view
                return;
            default:
                renderMessage('error', 'Error Loading Assessment', 'There was some trouble loading the assessment. Please try again later.')
                return;
        }
    }

    return (
        <div id='ancestorContainer' className={Styles.ancestorContainer}>
            {responseSubmitted === null && (
                <>
                    <StudentRegistration onSubmit={handleUserSubmit} loading={loading} />
                </>
            )}
            {responseSubmitted === false && (
                <>
                    {renderAssessment(assessmentData.type)}
                </>
            )}
            {responseSubmitted === true && (
                <>
                    {
                        quizCompleted
                            ? renderMessage('info', 'Response Submitted Successfully', 'Thank you for submitting the response. Your response has been recorded successfully. No further action needed from your side')
                            : renderMessage('info', 'Response Already Submitted', 'You have already submitted a response for this assessment. You may close this tab')
                    }
                </>
            )}
        </div>
    )

}